import { useMemo, useState } from 'react'
import { useAuroraStore } from '../../store/auroraStore'
import { rubricDomains, type PlanBlock, type RubricDomain } from '../../store/selectors'
import { removeBlock, updateBlock } from '../../lib/planEdit'
import { Sheet } from '../ui/Sheet'

/**
 * Editing one activity.
 *
 * The plan is a proposal, not an instruction — a parent who cannot change it
 * stops trusting it the first time it is wrong about their child. So every
 * field the agent wrote is editable here, including the reason, and the change
 * is undoable like every other plan edit.
 *
 * `why` gets the same weight as the title because it is the line that survives:
 * a parent who knows why they are doing something adapts it well in the moment.
 */

const DOMAINS = [
  { key: 'literacy', label: 'Literacy' },
  { key: 'language', label: 'Language' },
  { key: 'logic', label: 'Logic' },
  { key: 'math', label: 'Math' },
  { key: 'fineMotor', label: 'Fine motor' },
  { key: 'grossMotor', label: 'Gross motor' },
  { key: 'socialEmotional', label: 'Social' },
  { key: 'executiveFunction', label: 'Focus' },
  { key: 'creative', label: 'Creative' },
  { key: 'time', label: 'Time' },
  { key: 'science', label: 'Science' },
  { key: 'secondLanguage', label: 'Second language' },
  { key: 'selfCare', label: 'Self-care' },
]

const MINUTES = [5, 10, 15, 20, 30]

export function BlockEditor({
  date,
  block,
  onClose,
}: {
  date: string
  block: PlanBlock
  onClose: () => void
}) {
  const domains = useAuroraStore(rubricDomains) as RubricDomain[]

  const [title, setTitle] = useState(block.title)
  const [why, setWhy] = useState(block.why ?? '')
  const [instructions, setInstructions] = useState(block.instructions ?? '')
  const [minutes, setMinutes] = useState(block.minutes ?? 10)
  const [domain, setDomain] = useState(block.domain ?? '')
  const [skills, setSkills] = useState<string[]>(block.skills ?? [])
  const [confirmingRemove, setConfirmingRemove] = useState(false)

  /** Suggestions come from the child's own rubric, narrowed to the domain. */
  const suggestions = useMemo(() => {
    const pool = domain ? domains.filter((d) => d.key === domain) : domains
    return pool
      .flatMap((d) => d.skills.map((sk) => sk.name))
      .filter((n) => n && !skills.includes(n))
      .slice(0, 8)
  }, [domains, domain, skills])

  const save = () => {
    updateBlock(date, block.id, {
      title: title.trim() || block.title,
      why: why.trim(),
      instructions: instructions.trim(),
      minutes,
      domain: domain || undefined,
      skills,
    })
    onClose()
  }

  return (
    <Sheet
      open
      onClose={onClose}
      eyebrow="Activity"
      title={block.title}
      footer={
        <>
          <button className="btn btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save}>
            Save
          </button>
        </>
      }
    >
      <div className="form">
        <div className="field">
          <label className="field-label" htmlFor="be-title">
            What are we doing?
          </label>
          <input
            id="be-title"
            className="field-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="be-why">
            Why this one?
          </label>
          <textarea
            id="be-why"
            className="field-input"
            rows={2}
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            placeholder="One line. It is what you will remember at 8am."
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="be-how">
            How it goes
          </label>
          <textarea
            id="be-how"
            className="field-input"
            rows={5}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />
        </div>

        <div className="field">
          <span className="field-label">How long, honestly?</span>
          <div className="segmented" role="radiogroup" aria-label="Duration">
            {MINUTES.map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={minutes === m}
                className={`segment${minutes === m ? ' is-on' : ''}`}
                onClick={() => setMinutes(m)}
              >
                {m} min
              </button>
            ))}
          </div>
          <p className="field-hint">
            The honest number, not the hopeful one. Ten minutes that happen beat twenty that do not.
          </p>
        </div>

        <div className="field">
          <span className="field-label">Which area?</span>
          <div className="chips" role="radiogroup" aria-label="Area">
            {DOMAINS.map((d) => (
              <button
                key={d.key}
                type="button"
                role="radio"
                aria-checked={domain === d.key}
                className={`chip chip-swatch${domain === d.key ? ' is-active' : ''}`}
                style={{ '--domain-color': `var(--domain-${d.key})` } as React.CSSProperties}
                onClick={() => setDomain(domain === d.key ? '' : d.key)}
              >
                <span className="chip-dot" aria-hidden="true" />
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <span className="field-label">What is it building?</span>

          {skills.length > 0 && (
            <div className="chips">
              {skills.map((sk) => (
                <button
                  key={sk}
                  type="button"
                  className="chip is-active"
                  onClick={() => setSkills(skills.filter((s) => s !== sk))}
                  aria-label={`Remove ${sk}`}
                >
                  {sk} <span aria-hidden="true">×</span>
                </button>
              ))}
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="chips">
              {suggestions.map((sk) => (
                <button
                  key={sk}
                  type="button"
                  className="chip"
                  onClick={() => setSkills([...skills, sk])}
                >
                  + {sk}
                </button>
              ))}
            </div>
          )}

          {domains.length === 0 && (
            <p className="field-hint">
              Skills fill in here once Sapling has a picture of where she is.
            </p>
          )}
        </div>

        <div className="field-danger">
          {confirmingRemove ? (
            <div className="row row-2">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  removeBlock(date, block.id, block.title)
                  onClose()
                }}
              >
                Yes, take it off the day
              </button>
              <button className="btn btn-quiet btn-sm" onClick={() => setConfirmingRemove(false)}>
                Keep it
              </button>
            </div>
          ) : (
            <button className="btn btn-quiet btn-sm" onClick={() => setConfirmingRemove(true)}>
              Remove this activity
            </button>
          )}
        </div>
      </div>
    </Sheet>
  )
}
